import React, { useEffect, useState } from 'react';
import asyncBatch from 'async-batch';
import {
  useRecoilCallback,
  useRecoilStateLoadable,
  useSetRecoilState,
} from 'recoil';
import { sleep } from '../../../modules/Manage/pages/queue-edit-dialog/QueueEditForm';
import {
  methodNameEnum,
  refetchToggle,
} from '../../../../recoil/selectors/makePHPRequest';
import { Logger } from '../../../../common/Logger';
import { GridRowData } from '@mui/x-data-grid';
import { AxiosResponse } from 'axios';
import { ModalWithStyles } from '../../../components/modals/ModalWithStyles';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../../../services/rest/makeAPIRequest';
import { IUserAtom, user } from '../../../../recoil/atoms/user';

interface IWebhooksDeleteDialog {
  selected: GridRowData[];
  setSelected: React.Dispatch<React.SetStateAction<GridRowData[]>>;
  show: boolean;
  onHide: () => void;
}

export function WebhooksDeleteDialog({
  selected,
  setSelected,
  show,
  onHide,
}: IWebhooksDeleteDialog) {
  const setRefetchToggleForWebhooks = useSetRecoilState(
    refetchToggle(endpointEnum.GET_WEBHOOKS)
  );
  const [userAtomLoadable] = useRecoilStateLoadable<
    IUserAtom | Record<string, unknown>
  >(user);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;
  const [fetchState, setFetchState] = useState({
    state: 'hasValue',
    message: '',
  });
  const deleteWebhooks = useRecoilCallback(({ snapshot }) => async () => {
    const deleteResponses: AxiosResponse<any>[] = [];
    // show loading UI
    setFetchState({
      state: 'loading',
      message: '',
    });
    let errorMessage: any;
    // run as async since synchronous atom updates will only trigger one reload
    await asyncBatch(
      selected,
      async ({ id: webhookid }: { id: string }) => {
        // stop if error occurred
        const data = {
          call: methodNameEnum.DELETE_WEBHOOK,
          apikey: userAtomContents.apikey,
          businessid: userAtomContents.businessid,
          webhookid,
        };
        if (!errorMessage) {
          const response = await snapshot.getPromise(
            // makePHPRequest({
            //   methodName: methodNameEnum.DELETE_QUEUE,
            //   payloadJsonString: JSON.stringify({
            //     queueid,
            //   }),
            // transactionId: uuidv4(), // generate uuid to break selector cache
            // })
            makeAPIRequest({
              endpoint: endpointEnum.DELETE_WEBHOOK,
              payloadJsonString: JSON.stringify(data),
            })
            // generate uuid to break selector cache
          );
          deleteResponses.push(response);
          errorMessage = response?.data?.error;
          await sleep(100); // pause between calls to reduce load on database
        }
      },
      1 // one at a time
    );

    // clear selection and close modal on success
    if (!errorMessage) {
      // stop loading UI
      setFetchState({
        state: 'hasValue',
        message: '',
      });

      // clear selection
      setSelected([]);

      // refetch business webhooks
      setRefetchToggleForWebhooks({});

      // closing delete modal
      onHide();

      Logger.trace(
        {
          message: `Success deleteWebhook[${selected.length}]`,
          deleteResponses,
        },
        'deleteWebhooks'
      );
    } else {
      // stop loading UI
      // set error message
      setFetchState({
        state: 'hasError',
        message: errorMessage,
      });

      // log error and stop loading on failed
      Logger.error(
        {
          message: `Failed deleteWebhook[${selected.length}]`,
          errorMessage,
          deleteResponses,
        },
        'deleteWebhooks'
      );
    }
  });

  // if queues weren't selected we should close modal
  // for refreshing the page
  useEffect(() => {
    if (!selected || selected.length === 0) {
      onHide();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  return (
    <ModalWithStyles
      onSubmit={deleteWebhooks}
      title="Delete Webhook(s)"
      aria-labelledby="example-modal-sizes-title-md"
      show={show}
      onHide={onHide}
      isSubmitting={fetchState.state === 'loading'}
      isLoading={fetchState.state === 'loading'}
      size="xs"
      deleteDialog={true}
    >
      <>
        <p>
          You are about to delete {selected.length} Webhook(s), this procedure
          is not reversible.
        </p>
        <p>Do you want to proceed?</p>
      </>

      {fetchState.state === 'hasError' && (
        <span>Error deleting webhooks...</span>
      )}
    </ModalWithStyles>
  );
}
